import React, { useState, useEffect } from "react";
import { contactsAPI, companiesAPI } from "../../../services/api";
import { toast } from "react-toastify";
import Select from 'react-select';
import { handleDuplicateError, formatDuplicateMessage } from '../../../utils/duplicateCheckUtils';

const CreateContactModal = ({ onClose, onCreate, prefilledCompanyId }) => {
  const [loading, setLoading]                   = useState(false);
  const [companies, setCompanies]               = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [errors, setErrors]                     = useState({});
  const [touched, setTouched]                   = useState({});

  const [formData, setFormData] = useState({
    firstName:   "",
    lastName:    "",
    email:       "",
    phone:       "",
    companyName: null,
    jobTitle:    "",
    department:  "",
    linkedInUrl: "",
    leadStatus:  "",
    leadSource:  "",
  });

  /* ─────────────────────────────────────────
     FETCH COMPANIES
  ───────────────────────────────────────── */
  useEffect(() => {
    const fetchCompanies = async () => {
      setCompaniesLoading(true);
      try {
        const response = await companiesAPI.getAllCompanies();
        const formatted = (response.data || []).map(c => ({
          value: c.companyName,
          label: c.companyName,
          id:    c.id,
        }));
        setCompanies(formatted);

        if (prefilledCompanyId) {
          const match = formatted.find(c => c.id === prefilledCompanyId);
          if (match) setFormData(prev => ({ ...prev, companyName: match }));
        }
      } catch (err) {
        console.error("Failed to fetch companies:", err);
      } finally {
        setCompaniesLoading(false);
      }
    };
    fetchCompanies();
  }, [prefilledCompanyId]);

  /* ─────────────────────────────────────────
     VALIDATION
  ───────────────────────────────────────── */
  const validateField = (name, value) => {
    switch (name) {
      case "firstName": {
        if (!value.trim())            return "First name is required.";
        if (value.trim().length < 2)  return "First name must be at least 2 characters.";
        if (value.trim().length > 50) return "First name must not exceed 50 characters.";
        if (!/^[a-zA-Z\s'\-]+$/.test(value.trim()))
          return "First name can only contain letters, spaces, hyphens, or apostrophes.";
        return "";
      }
      case "lastName": {
        if (!value.trim())            return "Last name is required.";
        if (value.trim().length < 2)  return "Last name must be at least 2 characters.";
        if (value.trim().length > 50) return "Last name must not exceed 50 characters.";
        if (!/^[a-zA-Z\s'\-]+$/.test(value.trim()))
          return "Last name can only contain letters, spaces, hyphens, or apostrophes.";
        return "";
      }
      case "email": {
        if (!value.trim()) return "";
        if ((value.match(/@/g) || []).length !== 1)
          return "Email must contain exactly one '@' symbol.";
        if (!/^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/.test(value.trim()))
          return "Enter a valid email address (e.g. name@domain.com).";
        if (value.trim().length > 254)
          return "Email address is too long.";
        return "";
      }
      case "phone": {
        if (!value.trim()) return "";
        let normalized = value.replace(/\D/g, "");
        if (normalized.length === 12 && normalized.startsWith("91")) normalized = normalized.slice(2);
        if (normalized.length === 11 && normalized.startsWith("0")) normalized = normalized.slice(1);
        if (!/^\d+$/.test(normalized))
          return "Phone number must contain digits only.";
        if (normalized.length !== 10)
          return "Phone number must be exactly 10 digits.";
        if (!/^[6-9]/.test(normalized))
          return "Phone number must start with 6, 7, 8, or 9.";
        return "";
      }
      case "linkedInUrl": {
        if (!value.trim()) return "";
        if (!/^(https?:\/\/)?(www\.)?linkedin\.com\/.+$/.test(value.trim()))
          return "Enter a valid LinkedIn URL (e.g. https://linkedin.com/in/username).";
        return "";
      }
      case "jobTitle": {
        if (!value.trim()) return "";
        if (value.trim().length > 100)
          return "Job title must not exceed 100 characters.";
        return "";
      }
      default:
        return "";
    }
  };

  const validateAll = () => {
    const newErrors = {};
    ["firstName","lastName","email","phone","linkedInUrl","jobTitle"].forEach(field => {
      const err = validateField(field, formData[field]);
      if (err) newErrors[field] = err;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ─────────────────────────────────────────
     HANDLERS
  ───────────────────────────────────────── */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (touched[name]) {
      const err = validateField(name, value);
      setErrors(prev =>
        err ? { ...prev, [name]: err }
            : (({ [name]: _, ...rest }) => rest)(prev)
      );
    }
  };

  const handlePhoneChange = (e) => {
    let normalized = e.target.value.replace(/\D/g, "");
    if (normalized.length === 12 && normalized.startsWith("91")) normalized = normalized.slice(2);
    if (normalized.length === 11 && normalized.startsWith("0")) normalized = normalized.slice(1);
    const cleaned = normalized.slice(0, 10);
    setFormData(prev => ({ ...prev, phone: cleaned }));
    if (touched.phone) {
      const err = validateField("phone", cleaned);
      setErrors(prev =>
        err ? { ...prev, phone: err }
            : (({ phone: _, ...rest }) => rest)(prev)
      );
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const err = validateField(name, value);
    setErrors(prev =>
      err ? { ...prev, [name]: err }
          : (({ [name]: _, ...rest }) => rest)(prev)
    );
  };

  const handleEmailBlur = async (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const formatErr = validateField(name, value);
    if (formatErr) {
      setErrors(prev => ({ ...prev, [name]: formatErr }));
      return;
    }
    setErrors(prev => (({ [name]: _, ...rest }) => rest)(prev));
    if (!value.trim()) return;
    try {
      const res = await contactsAPI.checkEmailExists(value.trim());
      if (res.data?.exists) {
        setErrors(prev => ({ ...prev, email: formatDuplicateMessage('email') }));
      }
    } catch (error) {
      handleDuplicateError(error, (msg) => setErrors(prev => ({ ...prev, email: msg })));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  /* ─────────────────────────────────────────
     SUBMIT
  ───────────────────────────────────────── */
  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    setTouched({
      firstName: true, lastName: true, email: true,
      phone: true, linkedInUrl: true, jobTitle: true,
    });

    if (!validateAll()) {
      toast.warning("Please fix the highlighted errors before submitting.");
      setTimeout(() => {
        const el = document.querySelector('[data-error="true"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return;
    }

    setLoading(true);
    try {
      // ✅ Only send fields that have values — enums never sent if not selected
      const payload = {
        firstName: formData.firstName.trim(),
        lastName:  formData.lastName.trim(),

        // Optional strings — only include if filled
        ...(formData.email.trim()        && { email:       formData.email.trim().toLowerCase() }),
        ...(formData.phone.trim()        && { phone:       formData.phone.trim() }),
        ...(formData.jobTitle.trim()     && { jobTitle:    formData.jobTitle.trim() }),
        ...(formData.linkedInUrl.trim()  && { linkedInUrl: formData.linkedInUrl.trim() }),

        // ✅ Enums — only send if user actually selected a value
        ...(formData.department && { department: formData.department }),
        ...(formData.leadStatus && { leadStatus: formData.leadStatus }),
        ...(formData.leadSource && { leadSource: formData.leadSource }),

        // Company — only send if selected
        ...(formData.companyName?.value && { companyName: formData.companyName.value }),
        ...(formData.companyName?.id    && { companyId:   formData.companyName.id }),
      };

      await contactsAPI.addContact(payload);
      toast.success("Contact created successfully!");
      onCreate?.();
      onClose();

    } catch (error) {
      const status  = error.response?.status;
      const message = (error.response?.data?.message || "").toLowerCase();

      if (status === 409 || message.includes("duplicate") || message.includes("already exists")) {
        if (message.includes("phone") || message.includes("phone_number")) {
          setErrors(prev => ({ ...prev, phone: "This phone number is already linked to another contact." }));
          toast.error("📵 Phone number already in use — please enter a different number.");
        } else if (message.includes("email")) {
          setErrors(prev => ({ ...prev, email: "This email is already linked to another contact." }));
          toast.error("📧 Email address already in use — please enter a different email.");
        } else {
          toast.error("A contact with the same details already exists. Please review and try again.");
        }
      } else if (status === 400) {
        toast.error("Invalid data submitted. Please review all fields and try again.");
      } else if (status === 403) {
        toast.error("You don't have permission to create contacts.");
      } else if (status === 500) {
        toast.error("Server error. Please try again in a moment.");
      } else {
        toast.error(error.response?.data?.message || "Failed to create contact. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  /* ─────────────────────────────────────────
     UI HELPERS
  ───────────────────────────────────────── */
  const fieldCls = (name) =>
    `w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2
     focus:ring-[#1C4E6D] focus:border-transparent transition-colors ${
      errors[name] ? "border-red-400 bg-red-50" : "border-gray-300"
    }`;

  const labelCls = "block text-sm font-medium text-gray-700 mb-1";

  const ErrorMsg = ({ field }) =>
    errors[field] ? (
      <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
        <span>⚠</span> {errors[field]}
      </p>
    ) : null;

  /* ─────────────────────────────────────────
     RENDER
  ───────────────────────────────────────── */
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm">
      <div className="bg-white shadow-2xl w-full max-w-2xl h-full flex flex-col animate-slideInRight overflow-hidden">

        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">Create Contact</h2>
          <button onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition text-xl font-bold">✕</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">

          {/* ── Personal Information ── */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              {/* First Name */}
              <div data-error={!!errors.firstName}>
                <label className={labelCls}>
                  First Name <span className="text-red-500">*</span>
                </label>
                <input type="text" name="firstName" value={formData.firstName}
                  onChange={handleChange} onBlur={handleBlur} maxLength={50}
                  className={fieldCls("firstName")} placeholder="Enter first name" />
                <ErrorMsg field="firstName" />
              </div>

              {/* Last Name */}
              <div data-error={!!errors.lastName}>
                <label className={labelCls}>
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input type="text" name="lastName" value={formData.lastName}
                  onChange={handleChange} onBlur={handleBlur} maxLength={50}
                  className={fieldCls("lastName")} placeholder="Enter last name" />
                <ErrorMsg field="lastName" />
              </div>

              {/* Email */}
              <div data-error={!!errors.email}>
                <label className={labelCls}>Email</label>
                <input type="text" name="email" value={formData.email}
                  onChange={handleChange} onBlur={handleEmailBlur} maxLength={254}
                  className={fieldCls("email")} placeholder="email@example.com" />
                <ErrorMsg field="email" />
              </div>

              {/* Phone */}
              <div data-error={!!errors.phone}>
                <label className={labelCls}>Phone Number</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 select-none">
                    +91
                  </span>
                  <input
                    type="tel" inputMode="numeric" name="phone"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    onBlur={() => {
                      setTouched(p => ({ ...p, phone: true }));
                      const err = validateField("phone", formData.phone);
                      setErrors(p =>
                        err ? { ...p, phone: err }
                            : (({ phone: _, ...r }) => r)(p)
                      );
                    }}
                    maxLength={10}
                    className={`${fieldCls("phone")} pl-12`}
                    placeholder="9XXXXXXXXX"
                  />
                </div>
                <p className={`text-xs mt-1 text-right ${
                  formData.phone.length === 10 ? "text-green-600" : "text-gray-400"
                }`}>
                  {formData.phone.length}/10
                </p>
                <ErrorMsg field="phone" />
              </div>

            </div>
          </div>

          {/* ── Professional Information ── */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Professional Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              {/* Company */}
              <div>
                <label className={labelCls}>Company Name</label>
                <Select
                  options={companies}
                  value={formData.companyName}
                  onChange={(opt) => handleSelectChange("companyName", opt)}
                  isLoading={companiesLoading}
                  isClearable
                  placeholder="Select Company"
                  className="text-sm"
                  classNamePrefix="react-select"
                />
              </div>

              {/* Job Title */}
              <div data-error={!!errors.jobTitle}>
                <label className={labelCls}>Job Title</label>
                <input type="text" name="jobTitle" value={formData.jobTitle}
                  onChange={handleChange} onBlur={handleBlur} maxLength={100}
                  className={fieldCls("jobTitle")} placeholder="e.g. Senior Engineer" />
                <ErrorMsg field="jobTitle" />
              </div>

              {/* Department */}
              <div>
                <label className={labelCls}>Department</label>
                <select name="department" value={formData.department}
                  onChange={handleChange} className={fieldCls("department")}>
                  <option value="">Select Department</option>
                  {[
                    ["EXECUTIVE_LEADERSHIP","Executive Leadership"],
                    ["HUMAN_RESOURCES","Human Resources"],
                    ["TALENT_ACQUISITION","Talent Acquisition"],
                    ["SALES","Sales"],
                    ["MARKETING","Marketing"],
                    ["CUSTOMER_SUPPORT","Customer Support"],
                    ["FINANCE","Finance"],
                    ["LEGAL","Legal"],
                    ["IT","IT"],
                    ["ENGINEERING","Engineering"],
                    ["PRODUCT_MANAGEMENT","Product Management"],
                    ["DESIGN_UX","Design & UX"],
                    ["OPERATIONS","Operations"],
                    ["BUSINESS_DEVELOPMENT","Business Development"],
                    ["PROCUREMENT","Procurement"],
                    ["MANUFACTURING","Manufacturing"],
                    ["LOGISTICS","Logistics"],
                    ["QUALITY_ASSURANCE","Quality Assurance"],
                    ["RESEARCH_DEVELOPMENT","Research & Development"],
                    ["TRAINING_LD","Training & L&D"],
                    ["SECURITY_RISK","Security & Risk"],
                    ["PR_COMMUNICATIONS","PR & Communications"],
                    ["FACILITIES_MANAGEMENT","Facilities Management"],
                    ["CONSULTING","Consulting"],
                    ["HEALTHCARE","Healthcare"],
                    ["EDUCATION","Education"],
                    ["DATA_ANALYTICS","Data & Analytics"],
                    ["INVESTOR_RELATIONS","Investor Relations"],
                    ["CORPORATE_STRATEGY","Corporate Strategy"],
                    ["OTHER","Other"],
                  ].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>

              {/* LinkedIn */}
              <div data-error={!!errors.linkedInUrl}>
                <label className={labelCls}>LinkedIn URL</label>
                <input type="text" name="linkedInUrl" value={formData.linkedInUrl}
                  onChange={handleChange} onBlur={handleBlur}
                  className={fieldCls("linkedInUrl")}
                  placeholder="https://linkedin.com/in/..." />
                <ErrorMsg field="linkedInUrl" />
              </div>

            </div>
          </div>

          {/* ── Lead Information ── */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Lead Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              {/* Lead Status */}
              <div>
                <label className={labelCls}>Lead Status</label>
                <select name="leadStatus" value={formData.leadStatus}
                  onChange={handleChange} className={fieldCls("leadStatus")}>
                  <option value="">Select Status</option>
                  {[
                    ["NEW","New"],
                    ["PROSPECT","Prospect"],
                    ["CONTACTED","Contacted"],
                    ["QUALIFIED_LEAD","Qualified Lead"],
                    ["UNQUALIFIED_LEAD","Unqualified Lead"],
                    ["NURTURING","Nurturing"],
                    ["MEETING_SCHEDULED","Meeting Scheduled"],
                    ["PROPOSAL_SENT","Proposal Sent"],
                    ["NEGOTIATION","Negotiation"],
                    ["CONTRACT_SENT","Contract Sent"],
                    ["WON_CUSTOMER","Won Customer"],
                    ["LOST","Lost"],
                    ["ON_HOLD","On Hold"],
                    ["OTHER","Other"],
                  ].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>

              {/* Lead Source */}
              <div>
                <label className={labelCls}>Lead Source</label>
                <select name="leadSource" value={formData.leadSource}
                  onChange={handleChange} className={fieldCls("leadSource")}>
                  <option value="">Select Source</option>
                  {[
                    ["GOOGLE_ADS","Google Ads"],
                    ["LINKEDIN_ADS","LinkedIn Ads"],
                    ["FACEBOOK_ADS","Facebook Ads"],
                    ["INSTAGRAM_ADS","Instagram Ads"],
                    ["WHATSAPP_ADS","WhatsApp Ads"],
                    ["TELEGRAM_ADS","Telegram Ads"],
                    ["CONTACT_FORM","Contact Form"],
                    ["EMAIL_CAMPAIGN","Email Campaign"],
                    ["COLD_CALLING","Cold Calling"],
                    ["REFERRAL","Referral"],
                    ["EVENTS","Events"],
                    ["TRADE_SHOWS","Trade Shows"],
                    ["PARTNERSHIP","Partnership"],
                    ["ORGANIC_SEARCH","Organic Search"],
                    ["PAPER_ADS","Paper Ads"],
                    ["OTHER","Other"],
                  ].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>

            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="flex justify-end items-center gap-3 px-6 py-4 border-t bg-gray-50 flex-shrink-0">
          <button onClick={onClose}
            className="px-5 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition font-medium">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={loading}
            className="px-5 py-2 text-sm rounded-lg bg-[#1C4E6D] text-white hover:bg-[#153b52] transition font-medium disabled:opacity-50 disabled:cursor-not-allowed">
            {loading ? "Creating..." : "Create Contact"}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        .animate-slideInRight { animation: slideInRight 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default CreateContactModal;